import {useEffect, useRef, useState} from 'react';
import globalStore from '@/services/global.state';
import {runPollingPopupChecks} from '@/services/polling-popup-checks';

const POLL_MIN_MS = 30000;
const POLL_MAX_MS = 60000;

const isDocumentVisible = () =>
  typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * 按上次请求耗时收紧 30s–60s 随机区间：
 * - 慢网（≥2s）→ 45–60s
 * - 快网（≤500ms）→ 30–45s
 * - 其它 → 35–55s
 */
function getNextPollIntervalMs(lastDurationMs: number) {
  let min = 35000;
  let max = 55000;
  if (lastDurationMs >= 2000) {
    min = 45000;
    max = POLL_MAX_MS;
  } else if (lastDurationMs <= 500) {
    min = POLL_MIN_MS;
    max = 45000;
  }
  return min + Math.floor(Math.random() * (max - min + 1));
}

/**
 * 登录且页面可见时，随机间隔轮询未领取 Wave Cashback 奖励；
 * 有数据则弹出全局通知；不论是否出弹窗都会刷新余额。
 */
export function useWaveCashbackPolling() {
  const [hasToken, setHasToken] = useState(!!globalStore.token);
  const [documentVisible, setDocumentVisible] = useState(isDocumentVisible);
  const active = hasToken && documentVisible;
  const activeRef = useRef(active);
  const timerRef = useRef<number | null>(null);
  const inflightRef = useRef(false);
  const lastDurationRef = useRef(800);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    const sub = globalStore.tokenSubject.subscribe(token => {
      setHasToken(!!token);
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const update = () => setDocumentVisible(isDocumentVisible());
    update();
    document.addEventListener('visibilitychange', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!active) {
      clearTimer();
      return;
    }

    const scheduleNext = () => {
      clearTimer();
      if (!activeRef.current) {
        return;
      }
      const delay = getNextPollIntervalMs(lastDurationRef.current);
      timerRef.current = window.setTimeout(() => {
        void poll();
      }, delay);
    };

    const poll = async () => {
      if (!activeRef.current || !globalStore.token || inflightRef.current) {
        scheduleNext();
        return;
      }
      inflightRef.current = true;
      const startedAt = Date.now();
      try {
        // 不定时弹窗轮询：不论是否出弹窗都刷新余额，并附带检测破产补助
        await runPollingPopupChecks({refreshBalance: true});
        lastDurationRef.current = Date.now() - startedAt;
      } catch {
        lastDurationRef.current = Date.now() - startedAt;
      } finally {
        inflightRef.current = false;
        scheduleNext();
      }
    };

    // 进入可轮询状态后先拉一次，再进入随机间隔
    void poll();

    return () => {
      clearTimer();
    };
  }, [active]);
}
